export const groupBy = <T, K extends keyof any>(list: T[], getKey: (item: T) => K) =>
  list.reduce((previous, currentItem) => {
    const group = getKey(currentItem);
    if (!previous[group]) previous[group] = [];
    previous[group].push(currentItem);
    return previous;
  }, {} as Record<K, T[]>);

export const unique = <T>(arr: T[]) => {
  if (!arr || !arr.length) return arr;
  return arr.reduce((accumulator: T[], currentValue) => {
    return accumulator.indexOf(currentValue) > -1 ? accumulator : [...accumulator, currentValue];
  }, []);
}

/**
 * Analog for "classNames" function which works with any type. 
 * @returns array which items match given condition
 * 
 * @example
 * 
 * const a = 'yes'
 * const result = conditionalArray(1, 2, [3, a === 'yes']);
 * // result => [1, 2, 3]
 * 
 * const a = 'no'
 * const result = conditionalArray(1, 2, [3, a === 'yes']);
 * // result => [1, 2]
 */
export function conditionalArray<T>(...args: (T | [value: T, condition: boolean])[]): T[] {
  return args
    .filter(item => (!Array.isArray(item)) || (Array.isArray(item) && item[1]))
    .map(item => Array.isArray(item) ? item[0] : item);
}

export function convertToArray<T>(items: (T | T[])) {
  if (Array.isArray(items)) {
    return items;
  } else{
    return [items]
  }
}

export function filterUndefined<T>(ts: (T | undefined)[]): T[] {
  return ts.filter((t: T | undefined): t is T => !!t)
}

export function* takeWhile<T>(fn: (item: T) => boolean, xs: T[]) {
  for (let x of xs) {
    if (fn(x)) {
      yield x;
    } else {
      break;
    }
  }
}

/**
 * Swap elements of array. Mutates original array.
 * @param array Array where need to swap elements
 * @param index1 First element index
 * @param index2 Second element index
 */
export function swap<T>(array: T[], index1: number, index2: number) {
  [array[index1], array[index2]] = [array[index2], array[index1]];
}

export function isNotEmpty<T>(a?: T[]): boolean {
  return Array.isArray(a) && a.length > 0;
}

export function intersect<T>(array1: T[], array2: T[]): T[] {
  return array1.filter(value => array2.includes(value));
}

export function areEqual<T>(a: T[], b: T[], comparer: (a: T, b: T) => boolean = (a, b) => a === b): boolean {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; ++i) {
    if (!comparer(a[i], b[i])) {
      return false;
    }
  }
  return true;
}

/**
 * Find how many times a value appears in an array.
 *
 * For more detail about more values consider d3-array.rollup
 *
 * @param array to search
 * @param value to search for dupliacates
 * @param comparator optional for primitive values
 * @returns how many times the a value appears in an array
 */
export function includesCount<T>(array: T[], value: T, comparator?: (a: T, b: T) => boolean): number {
  return array.filter(entry => {
    return comparator ? comparator(entry, value) : entry === value;
  }).length;
}

// FWIW: since we are using d3 via NIVO for charts, it makes sense to also make use of d3.array helpers where useful:
// https://github.com/d3/d3-array/blob/master/README.md
// e.g. sorting numbers ascending / descending: import { ascending } from 'd3-array';
